<template>
	<div v-if="studentsCopy" class="report-page card-page">
		<content v-if="studentsCopy.length" ref="contentWrapper">
			<div
				v-for="studentInfo in studentsCopy"
				ref="pdfContent"
				:key="studentInfo.id"
				class="print-preview"
			>
				<div ref="firstPage" class="pdf-page-wrapper">
					<header
						style="display: flex; align-items:center; justify-content: center;"
						role="banner"
					>
						<img
							src="@/assets/images/Logo_JA.svg"
							alt="Junior Achievement Logo"
							class="ja-logo"
						/>
					</header>
					<h1>{{ $t('studentReportTitle') }}</h1>
					<div class="student-info-wrapper">
						<div class="avatar-wrapper">
							<ja-avatar
								v-if="studentInfo.avatar"
								aria-hidden="true"
								:image="studentInfo.avatar.largeImageBase64"
							></ja-avatar>
							<img
								v-if="
									!studentInfo.isEntryVirtualSimulationType &&
										studentInfo.studentSmileScoreData &&
										studentInfo.studentSmileScoreData.message
								"
								class="financial-status"
								:alt="
									$t('financialHealthStatus', {
										status: studentInfo.studentSmileScoreData.message
									})
								"
								:src="
									require(`@/assets/images/emotes/${studentInfo.studentSmileScoreData.message.toLowerCase()}.png`)
								"
							/>
						</div>
						<div class="student-info">
							<div class="label body-text-1">
								{{ $t('studentInformation') }}
							</div>
							<div
								v-for="field in studentInfoFields"
								:key="field.name"
								class="info-entry"
							>
								<div class="label body-text-2">
									{{ field.name }}
								</div>
								<div class="body-text-2">
									{{ getFieldValue(field, studentInfo) }}
								</div>
								<div class="body-text-3">
									{{ getFieldSubValue(field, studentInfo) }}
								</div>
							</div>
						</div>
					</div>
					<div class="container-career-goals">
						<div class="career-info">
							<div class="label body-text-1">
								{{ $t('careerEducationLbl') }}
							</div>
							<div class="body-text-1">
								{{
									studentInfo.cmsEducation ? studentInfo.cmsEducation.name : ''
								}}
							</div>

							<div
								v-for="(field, index) in getCareerInfoFields(studentInfo)"
								:key="field.value + index"
								class="info-entry"
								:class="
									field.name === $t('nmiLbl') ||
									field.name === $t('householdNMILbl')
										? 'nmi-entry'
										: ''
								"
							>
								<div class="label body-text-2">
									{{ field.name }}
								</div>
								<div class="body-text-2">
									{{ field.value }}
								</div>
							</div>
						</div>
						<div class="simulation-info">
							<div class="label body-text-1">
								{{ $t('futureAndGoals') }}
							</div>
							<div
								v-for="field in simulationInfoFields(studentInfo)"
								:key="field.name"
								class="info-entry"
							>
								<div class="label body-text-2">
									{{ field.name }}
								</div>
								<div class="body-text-2">
									{{ getFieldValue(field, studentInfo) }}
								</div>
							</div>
						</div>
					</div>
					<div class="dashboard-wrapper">
						<h2 class="heading-3">{{ $t('budgetingSection') }}</h2>
						<div class="categories-grid" role="table">
							<div class="grid-row" role="row">
								<div class="header body-text-1" role="columnheader">
									{{ $t('expenseCategories') }}
								</div>
								<div
									v-for="stage in studentInfo.availableLifeStages"
									:key="stage.lifeStage_ID"
									class="header body-text-1"
									role="columnheader"
								>
									<span>{{ stage.name }}</span>
									<div class="subheader body-text-3">
										{{ $t('reportTableSubheader') }}
									</div>
								</div>
							</div>
							<div
								v-for="(category,
								index) in studentInfo.studentSummaryReportExpenseCategories"
								:key="index"
								class="grid-row"
								role="row"
							>
								<div
									:key="`${category.expenseCategoryName}`"
									:class="[
										'category-name',
										'grid-item',
										'body-text-1',
										{ odd: index % 2 == 0 }
									]"
									role="cell"
								>
									{{ category.expenseCategoryName }}
								</div>
								<div
									v-for="(data, dataIndex) in category.datas"
									:key="`${category.expenseCategoryName}_${dataIndex}`"
									:class="['grid-item', 'body-text-1', { odd: index % 2 == 0 }]"
									role="cell"
								>
									{{ getCategoryBudgetLabel(data) }}
								</div>
							</div>
						</div>
					</div>
				</div>
				<div ref="secondPage" class="pdf-page-wrapper">
					<div class="financial-overview">
						<h2 class="heading-3">{{ $t('financialOverview') }}</h2>
						<div class="charts-wrapper">
							<div class="chart-wrapper">
								<ja-pie-chart
									class="chart"
									:data="getStudentSavings(studentInfo)"
									:inline="true"
								></ja-pie-chart>
							</div>
							<div class="chart-wrapper">
								<ja-pie-chart
									v-if="
										studentInfo.isApprovedAndUsedForMortgageLoan ||
											studentInfo.autoLoanAmounts.length >= 1 ||
											studentInfo.sumOfAllCreditCardPaymentsForSimulation > 0 ||
											studentInfo.totalStudentLoan > 0
									"
									class="chart"
									:data="getStudentDebts(studentInfo)"
									:inline="true"
								></ja-pie-chart>
							</div>
						</div>
					</div>
					<div ref="logo" class="logo-wrapper">
						<ja-national-sponsor
							:external-sim-type="simulationType"
						></ja-national-sponsor>
					</div>
				</div>
			</div>
		</content>
		<content v-else class="no-results">
			<h1>{{ $t('noResultsFound') }}</h1>
		</content>
		<footer>
			<div class="footer-item">
				<ja-national-sponsor
					:external-sim-type="simulationType"
				></ja-national-sponsor>
			</div>
			<ja-button
				name="print"
				class="small outlined"
				:disabled="!studentsCopy.length"
				:aria-label="$t('downloadReport')"
				@click.native="generateReport"
				>{{ $t('download') }}</ja-button
			>

			<ja-button
				v-if="isContinueVisible"
				name="continue"
				class="small"
				:aria-label="$t('continueFinalPage')"
				@click.once.native="endSimulation"
				>{{ $t('continue') }}</ja-button
			>
			<ja-button
				v-else-if="isBackToReportsVisible"
				class="small"
				name="backToReports"
				@click.native.once="backToReportPage"
			>
				{{ $tc('backToReportLink', 2) }}
			</ja-button>
			<ja-button
				v-else
				name="close"
				class="small"
				@click.once.native="navigateToReporting"
				>{{ $t('close') }}</ja-button
			>
		</footer>
		<ja-modal :show="showStudentSelectionModal" class="modal-wrapper">
			<div class="modal-selection">
				<h3 class="heading-3">{{ $t('selectStudentsToDownload') }}</h3>
				<div class="modal-selection__content">
					<div class="select-student">
						<label>
							<input
								v-model="selectedAllStudents"
								class="select-student__checkbox"
								type="checkbox"
								@change="handleSelectedAll"
							/>
							{{ $t('titleAllStudents') }}
						</label>
					</div>
					<div
						v-for="student in studentSelection"
						ref="modalCheckbox"
						:key="student.id"
						class="select-student"
					>
						<label>
							<input
								class="select-student__checkbox"
								type="checkbox"
								:value="student.id"
							/>
							{{ student.name }}
						</label>
					</div>
				</div>
				<div class="btn-group btn-group--center">
					<ja-button
						name="confirm"
						class="small primary"
						@click.native="printSelectedStudents"
					>
						{{ $t('confirm') }}
					</ja-button>
				</div>
			</div>
		</ja-modal>
	</div>
</template>
<script>
import gql from 'graphql-tag';
import '@/components/icons/multiselect-deselected';
import '@/components/icons/multiselect-selected';
import Avatar from '@/components/ui/Avatar';
import Button from '@/components/ui/Button';
import NationalSponsor from '@/components/ui/NationalSponsor';
import PieChart from '@/components/Chart/PieChart';
import AdminFacility from '@/mixins/AdminFacility';
import NavigationLoading from '@/mixins/NavigationLoading';
import Modal from '@/components/Modal/Modal';
import {
	getChooseSimulation,
	swapChooseSimulation,
	setStorageData,
	getStorageData,
	removeStorageData,
	removeStorageReportSim,
	getStorageReportSim
} from '@/helpers/storageFunctions';
import { defaultNames } from '@/helpers/constants';
import axios from 'axios';
import CryptoJS from 'crypto-js';
//import { getCurrentInstance } from 'vue';

export default {
	components: {
		jaNationalSponsor: NationalSponsor,
		jaAvatar: Avatar,
		jaPieChart: PieChart,
		jaButton: Button,
		jaModal: Modal
	},
	mixins: [NavigationLoading, AdminFacility],
	data() {
		return {
			students: null,
			studentsCopy: [],
			studentsTotal: [],
			categories: null,
			filters: sessionStorage.getItem('reportFilters')
				? JSON.parse(sessionStorage.getItem('reportFilters'))
				: null,
			backToReportsPageAvailable: false,
			continueQuery: false,
			showStudentSelectionModal: false,
			studentSelection: null,
			modalCheckbox: null,
			selectedAllStudents: false,
			selectedItems: [],
			JA_LOCALIZATION_IRL: this.$store.getters.isIrl,
			queryStudentsFind: false,
			printingInProgress: false,
			batchSize: 0
		};
	},
	computed: {
		lifeStageOrders() {
			return {
				Teenager: 0,
				'Young Adult': 1,
				'Working Adult': 2,
				'Adult Nearing Retirement': 3
			};
		},
		simulationType() {
			if (this.filters) {
				return this.filters.simulationType;
			}
			return null;
		},
		isContinueVisible() {
			return (
				this.userData &&
				this.userData.role == 'Student' &&
				!this.backToReportsPageAvailable
			);
		},
		isBackToReportsVisible() {
			return this.backToReportsPageAvailable;
		},
		reportVersion() {
			if (this.filters) {
				return this.filters.reportVersion;
			}
			return null;
		},
		studentInfoFields() {
			return [
				{
					name: this.$t('studentNameLbl')
				},
				{
					name: this.$t('teacherNameLbl')
				},
				{
					name: this.$t('studentEmailLbl')
				},
				{
					name: this.$t('schoolNameLbl')
				},
				{
					name: this.$t('simDateLbl')
				}
			];
		}
	},
	watch: {
		students(value) {
			if (value) {
				value.map(student => {
					student.studentSummaryReportExpenseCategories = student.studentSummaryReportExpenseCategories.filter(
						category => {
							const name = category.defaultName ?? category.expenseCategoryName;
							return !name
								.toLowerCase()
								.includes(defaultNames.AUTO_LOAN.toLowerCase());
						}
					);
					/*student.availableLifeStages.sort((a, b) => {
						return a.displayOrder - b.displayOrder;
					});*/
					student.studentSummaryReportExpenseCategories.forEach(category => {
						category.datas.sort((a, b) => {
							const lifeStageA = a.lifeStageName;
							const lifeStageB = b.lifeStageName;
							const orderA = this.lifeStageOrders[lifeStageA];
							const orderB = this.lifeStageOrders[lifeStageB];
							return orderA - orderB;
						});
					});
					return;
				});
				this.studentsCopy = this.studentsTotal;
				if (this.userData.role !== 'Student') {
					this.$nextTick(() => {
						const contentWrapper = this.$refs.contentWrapper;
						if (contentWrapper) {
							contentWrapper.addEventListener('scroll', this.handleScroll);
						}
					});
					if (this.batchSize == 0) {
						this.batchSize = value.length > 0 ? value.length : 1;
					}
				}
			}
		}
	},
	async created() {
		this.backToReportsPageAvailable = getStorageData('backToReports');
		if (this.userData && this.userData.role != 'Student' && !this.filters) {
			this.$router.push({ name: 'reporting' });
		}

		if (this.selectedFacility == null) {
			const key = CryptoJS.SHA256(this.userData.email).toString();
			const myFacility = localStorage.getItem(key)
				? JSON.parse(localStorage.getItem(key))
				: null;

			this.$store.dispatch('updateSelectedFacility', myFacility);
		}

		this.students = await this.getNewReport();

		// this will trigger the query because the same variable is used in
		// skip() function.
		this.continueQuery = this.reportVersion === 0;
		window.addEventListener('afterprint', () => {
			if (this.printingInProgress) {
				this.studentsCopy = this.studentsTotal;
				this.printingInProgress = false;
			}
		});
	},
	methods: {
		simulationInfoFields(studentInfo) {
			const fields = [
				{
					name: this.$t('creditScoreLbl')
				},
				{
					name: this.$t('lifeStageLbl')
				},
				{
					name: this.$t('familyLbl')
				},
				{
					name: this.$t('childrenLbl')
				}
			];

			if (
				!studentInfo.isEntryVirtualSimulationType &&
				studentInfo.studentGoals &&
				studentInfo.studentGoals.length > 1
			) {
				fields.push({
					name: this.$t('goalsLbl')
				});
			}
			fields.push({
				name: this.$t('interestsLbl')
			});

			const filteredFields = this.$store.getters.isIrl
				? fields.filter(item => item.name !== this.$t('creditScoreLbl'))
				: fields;

			return filteredFields;
		},
		getFieldSubValue(field, studentInfo) {
			if (field.name == this.$t('studentNameLbl')) {
				return `(${studentInfo.firstName} ${studentInfo.lastName})`;
			} else if (field.name == this.$t('teacherNameLbl')) {
				return this.getTeacherName(studentInfo);
			} else if (field.name == this.$t('schoolNameLbl')) {
				return !studentInfo.currentSimulation.school
					? ''
					: `(${studentInfo.currentSimulation.school})`;
			}
			return null;
		},
		getFieldValue(field, studentInfo) {
			if (field.name == this.$t('studentNameLbl')) {
				return studentInfo.formStudentName;
			} else if (field.name == this.$t('teacherNameLbl')) {
				return studentInfo.formTeacherName;
			} else if (field.name == this.$t('studentEmailLbl')) {
				return studentInfo.email;
			} else if (field.name == this.$t('schoolNameLbl')) {
				return studentInfo.formSchoolName;
			} else if (field.name == this.$t('simDateLbl')) {
				return studentInfo.currentSimulation
					? this.$options.filters.dateFormatNumeric(
							studentInfo.currentSimulation.startDate
					  )
					: '';
			} else if (field.name == this.$t('creditScoreLbl')) {
				return studentInfo.currentCreditScore;
			} else if (field.name == this.$t('lifeStageLbl')) {
				return studentInfo.cmsLifeStage ? studentInfo.cmsLifeStage.name : '';
			} else if (field.name == this.$t('familyLbl')) {
				return this.getStudentFuture(studentInfo);
			} else if (field.name == this.$t('childrenLbl')) {
				return studentInfo.studentChildren.length;
			} else if (field.name == this.$t('goalsLbl')) {
				return this.getStudentGoals(studentInfo);
			} else if (field.name == this.$t('interestsLbl')) {
				return this.getStudentInterests(studentInfo);
			}
		},
		getCareerInfoFields(studentInfo) {
			const availableLifeStages = [...studentInfo.availableLifeStages];
			const listNMI = studentInfo.netMonthlyIncomeList;

			const listNMISpouse = studentInfo.spouseNetMonthlyIncomeList
				? studentInfo.spouseNetMonthlyIncomeList
				: [];

			const studentJobTitles = studentInfo.cmsCareer
				? studentInfo.cmsCareer.cmsCareerJobTitles
				: [];

			const filteredJobTitles = studentJobTitles.filter(job =>
				availableLifeStages.find(
					ls => ls.lifeStage_ID == job.cmsLifeStage.lifeStage_ID
				)
			);

			const createDataJobTitle = filteredJobTitles.map(job => ({
				name: this.$t('jobTitleLbl'),
				value: job.jobTitle,
				lifeStage: availableLifeStages.find(
					ls => ls.lifeStage_ID == job.cmsLifeStage.lifeStage_ID
				)
			}));

			const createDataNMI = listNMI.map(NMI => ({
				name: this.$t('nmiLbl'),
				value: `${this.$t('currencySymbol')} ${NMI.value.toLocaleString(
					'en-US'
				)} `,
				lifeStage: availableLifeStages.find(
					lifeStage => NMI.key == lifeStage.name
				)
			}));

			const createDataNMISpouse = listNMISpouse.map(NMI => ({
				name: this.$t('householdNMILbl'),
				value: `${this.$t('currencySymbol')} ${parseInt(
					NMI.value.toFixed(0)
				).toLocaleString('en-US')} `,
				lifeStage: availableLifeStages.find(
					lifeStage => NMI.key == lifeStage.name
				)
			}));

			const combinedData = createDataJobTitle.concat(
				createDataNMI,
				createDataNMISpouse
			);

			/*combinedData.sort((a, b) => {
				const lifeStageA = a.lifeStage;
				const lifeStageB = b.lifeStage;
				const orderA = lifeStageA.displayOrder;
				const orderB = lifeStageB.displayOrder;
				if (orderA === orderB) {
					if (a.name === this.$t('jobTitleLbl')) {
						return -1;
					} else {
						return 1;
					}
				} else {
					return orderA - orderB;
				}
			});*/

			return combinedData;
		},
		getCategoryBudgetLabel(category) {
			const actual =
				category.actual !== null
					? `${this.$t('currencySymbol')}${category.actual.toLocaleString(
							'en-US'
					  )}`
					: this.$t('enDash');
			const budgeted =
				category.budgeted !== null
					? `${this.$t('currencySymbol')}${category.budgeted.toLocaleString(
							'en-US'
					  )}`
					: this.$t('enDash');
			return `${actual}/${budgeted}`;
		},
		getStudentGoals(studentInfo) {
			const studentGoals = studentInfo.studentGoals
				.filter(goal => {
					return !!goal.cmsGoal;
				})
				.map(goal => {
					return goal.cmsGoal.name;
				});

			return studentGoals.length ? `${studentGoals.join(', ')}` : '';
		},
		getStudentInterests(studentInfo) {
			const studentInterests = [];
			studentInfo.studentInterests.map(interest => {
				studentInterests.push(interest.cmsInterest.name);
			});
			return studentInterests.length ? `${studentInterests.join(', ')}` : '';
		},
		getStudentFuture(studentInfo) {
			const futureInfo = [];

			if (studentInfo.cmsMaritalStatus) {
				futureInfo.push(studentInfo.cmsMaritalStatus.status);
			}

			if (studentInfo.studentChildren.length) {
				futureInfo.push(
					studentInfo.studentChildren.length == 1
						? `${studentInfo.studentChildren.length} ${this.$t('child')}`
						: `${studentInfo.studentChildren.length} ${this.$t('children')}`
				);
			}

			if (studentInfo.numberOfPets > 0) {
				futureInfo.push(
					studentInfo.numberOfPets == 1
						? `${studentInfo.numberOfPets} ${this.$t('pet')}`
						: `${studentInfo.numberOfPets} ${this.$t('pets')}`
				);
			}

			return futureInfo.length ? `${futureInfo.join(' ')}` : '';
		},
		getStudentCareer(studentInfo) {
			const careerInfo = [];
			if (studentInfo.cmsCareerJobTitle) {
				careerInfo.push(studentInfo.cmsCareerJobTitle.jobTitle);
			}
			if (studentInfo.cmsEducation) {
				careerInfo.push(studentInfo.cmsEducation.name);
			}

			return careerInfo.length ? `${careerInfo.join(' ')}` : '';
		},
		getTeacherName(studentInfo) {
			if (
				studentInfo.currentSimulation !== null &&
				studentInfo.currentSimulation.userSimulations !== null &&
				studentInfo.currentSimulation.userSimulations.length > 0
			) {
				const educator = studentInfo.currentSimulation.userSimulations[0].user;
				return `${educator.firstName} ${educator.lastName}`;
			}
			return '';
		},
		getStudentSavings(studentInfo) {
			const checkingAccountAmount =
				studentInfo.netMonthlyIncome +
				studentInfo.spouseNetMonthlyIncome -
				studentInfo.totalExpenses;
			return {
				ariaLabel: this.$t('finalFinanceChart'),
				dataset: [
					{
						title: this.$t('remainingBalance'),
						value: checkingAccountAmount,
						ariaLabel: this.$t('checkingAccountAmount', {
							amount: checkingAccountAmount
						})
					}
				].concat(
					studentInfo.studentGoals.map(item => {
						let savingsName = item.cmsGoal
							? item.cmsGoal.name
							: this.$t('general');
						return {
							title: this.$t('savingsChartLbl', {
								savingName: savingsName
							}),
							value: item.totalSavingsIncludingCurrentLifeStage,
							ariaLabel: this.$t('savingsAccountAmount', {
								goal: savingsName,
								amount: item.totalSavingsIncludingCurrentLifeStage
							})
						};
					})
				)
			};
		},
		getStudentDebts(studentInfo) {
			const totalAutoLoans = studentInfo.autoLoanAmounts.reduce(
				(total, item) => total + parseInt(item.value),
				0
			);

			const autoLoansMsg = this.getAutoLoanMessage(studentInfo.autoLoanAmounts);

			const autoLoans =
				studentInfo.isApprovedAndUsedForAutoLoan ||
				studentInfo.isApprovedAndUsedForAutoLoanDealership
					? totalAutoLoans
					: null;

			const mortgageLoan = studentInfo.isApprovedAndUsedForMortgageLoan
				? studentInfo.mortgageLoanAmount
				: null;

			const studentTotalLoans =
				studentInfo.totalStudentLoan +
				studentInfo.sumOfAllCreditCardPaymentsForSimulation +
				mortgageLoan +
				totalAutoLoans;

			let result = {
				title: this.$t('totalLoans'),
				fullAmount: studentTotalLoans,
				ariaLabel: this.$t('totalLoans'),
				dataset: [
					{
						title: this.$t('postSecondaryLoanDebt'),
						value: studentInfo.totalStudentLoan,
						ariaLabel: this.$t('LoanApprovalsAmount', {
							amount: studentInfo.totalStudentLoan
						})
					},
					{
						title: this.$t('creditCardLoans'),
						value: studentInfo.sumOfAllCreditCardPaymentsForSimulation,
						ariaLabel: this.$t('LoanApprovalsAmount', {
							amount: studentInfo.sumOfAllCreditCardPaymentsForSimulation
						})
					},
					{
						title: this.$t('mortgageLoan'),
						value: mortgageLoan,
						ariaLabel: this.$t('LoanApprovalsAmount', {
							amount: mortgageLoan
						})
					},
					{
						title: this.$t('autoLoans'),
						value: autoLoans,
						ariaLabel: this.$t('LoanApprovalsAmount', {
							amount: autoLoans
						}),
						legendAdditionalInfo: autoLoansMsg
					}
				]
			};
			if (
				studentInfo.cmsEducation === null ||
				studentInfo.cmsEducation.tuition === null
			) {
				const studentLoanElementIndex = result.dataset.findIndex(
					x => x.title === this.$t('studentLoan')
				);
				if (studentLoanElementIndex !== -1) {
					result.dataset.splice(studentLoanElementIndex, 1);
				}
			}

			result.dataset = result.dataset.filter(
				item => item.value !== null && item.value !== 0
			);

			return result;
		},
		getDebtMessage(amountPaid, amountOwed) {
			if (amountPaid >= amountOwed) {
				return this.$t('fullyPayed');
			}
			return `${this.$t('remainingBalance')}: ${this.$t('currencySymbol')}${(
				amountOwed - amountPaid
			).toLocaleString('en-US')}`;
		},
		getAutoLoanMessage(data) {
			let result = '';
			for (const item of data) {
				result += `${item.key}: ${this.$t(
					'currencySymbol'
				)}${item.value.toLocaleString('en-US')}\n`;
			}
			return result;
		},
		async endSimulation() {
			const response = await this.$apollo.mutate({
				mutation: gql`
					mutation($update: StudentInputObjectGraphType!) {
						updateIgnoreNullValuesStudent(update: $update) {
							isSummaryReportShown
						}
					}
				`,
				variables: {
					update: {
						id: this.userData.studentId,
						isSummaryReportShown: true
					}
				}
			});
			if (response.data) {
				this.$store.commit('setLoading', true);

				this.$router.push({ name: 'congratulations' });
			}
		},
		handleSelectedAll() {
			if (this.selectedAllStudents) {
				this.$refs.modalCheckbox.forEach(el => {
					const checkbox = el.querySelector('.select-student__checkbox');
					checkbox.checked = true;
				});
			} else {
				this.$refs.modalCheckbox.forEach(el => {
					const checkbox = el.querySelector('.select-student__checkbox');
					checkbox.checked = false;
				});
			}
		},
		async generateReport() {
			if (this.studentsCopy.length === 1) {
				window.print();
			} else if (this.studentsCopy.length >= 2) {
				while (!this.queryStudentsFind) {
					if (this.reportVersion == 0) {
						this.students = await this.getOldReport();
					} else {
						this.students = await this.getNewReport();
					}

					if (this.queryStudentsFind) {
						this.$store.commit('setLoading', false);
						break;
					}
				}
				if (!this.studentSelection) {
					this.studentSelection = this.studentsCopy.map(student => ({
						id: student.id,
						name: `${student.firstName} ${student.lastName}`
					}));
				}
				this.selectedItems = [];
				await this.$nextTick();
				this.showStudentSelectionModal = true;
			}
		},
		async printSelectedStudents() {
			this.selectedItems = [];
			this.$refs.modalCheckbox.forEach(el => {
				const checkbox = el.querySelector('.select-student__checkbox');
				if (checkbox.checked) {
					this.selectedItems.push(checkbox.value);
				}
			});
			const selectedStudents = this.studentsCopy.filter(student =>
				this.selectedItems.includes(student.id.toString())
			);
			if (selectedStudents && selectedStudents.length) {
				this.studentsCopy = selectedStudents;
			}
			this.showStudentSelectionModal = false;
			this.printingInProgress = true;
			await this.$nextTick();
			window.print();
		},
		navigateToReporting() {
			this.$router.push({ name: 'reporting' });
		},
		backToReportPage() {
			removeStorageData('backToReports');
			if (getChooseSimulation().id) {
				swapChooseSimulation();
			}

			if (getStorageReportSim()) {
				removeStorageReportSim();
			}

			this.$store.commit('setLoading', true);

			this.$router.push({ name: 'simReport' });
		},
		async loadMoreStudents() {
			if (!this.queryStudentsFind) {
				if (this.reportVersion == 0) {
					this.students = await this.getOldReport();
				} else {
					this.students = await this.getNewReport();
				}

				this.$store.commit('setLoading', false);
			}
		},
		handleScroll() {
			this.contentWrapper = this.$refs.contentWrapper;
			const contentWrapper = this.$refs.contentWrapper;
			if (
				Math.round(contentWrapper.scrollTop) + contentWrapper.clientHeight >=
				contentWrapper.scrollHeight
			) {
				this.loadMoreStudents();
			}
		},
		getQueryString() {
			if (this.userData && this.userData.role == 'Student') {
				return `studentId=${this.userData.studentId}`;
			} else {
				const filters = [];
				filters.push(`skip=${this.studentsCopy.length}`);
				if (this.filters.student) {
					//filters.push('')
				}
				if (this.selectedFacilityId != null) {
					filters.push(`facility_ID=${this.selectedFacilityId}`);
				}
				if (this.filters.simulationType) {
					filters.push(`simulationType=${this.filters.simulationType}`);
				}
				if (this.filters.school) {
					filters.push(`schoolName=${this.filters.school}`);
				}
				if (this.filters.simulationClass) {
					filters.push(`sessionName= ${this.filters.simulationClass}`);
				}
				if (this.filters.educator) {
					filters.push(`educatorName=${this.filters.educator}`);
				}
				if (this.filters.student) {
					filters.push(`studentName=${this.filters.student}`);
				}
				if (this.filters.startDate) {
					filters.push(`startDate=${this.filters.startDate}`);
					//filters.push(`simulation.startDate <= "${this.filters.endDate}"`);
				}
				if (this.filters.startVisitDate) {
					filters.push(`startDate=${this.filters.startVisitDate}`);
					//filters.push(`simulation.startDate <= "${this.filters.endDate}"`);
				}
				return filters.join('&&');
			}
		},
		async getNewReport() {
			this.$store.commit('setLoading', true);
			const API_URL = process.env.VUE_APP_API_URL;
			const queryString = this.getQueryString();
			const response = await axios.get(
				`${API_URL}/api/Report/SimulationSummaryReport?${queryString}`,
				{
					withCredentials: true,
					headers: {
						'Cache-control': 'no-cache'
					}
				}
			);
			this.$store.commit('setLoading', false);
			const data = response.data;
			if (this.userData.role !== 'Student') {
				this.queryStudentsFind = data.length < this.batchSize;
			}
			this.studentsTotal.push(...data);
			return data;
		}
	},
	beforeRouteEnter(to, from, next) {
		if (from.name === 'simReport') {
			setStorageData('backToReports', true);
		}

		next(vm => {
			vm.fromRoute = from.name;
		});
	}
};
</script>
<style lang="scss" scoped>
.report-page {
	content {
		&.no-results {
			display: flex;
			justify-content: center;
			align-items: center;
		}
		.print-preview {
			max-width: 800px;
			margin: auto;
			width: 100%;
			display: flex;
			flex-direction: column;
			overflow-y: auto;
			align-items: center;
			page-break-after: always;

			.pdf-page-wrapper {
				width: 100%;
				display: flex;
				flex-direction: column;
				align-items: center;
			}

			// !important is used to override the standard font styling resizing, because of exporting the report as pdf
			h1 {
				font-size: 23px !important;
				line-height: 34.5px !important;
				letter-spacing: 0.25px !important;
			}
			.heading-3 {
				font-size: 17px !important;
				line-height: 25.5px !important;
				letter-spacing: 0.15px !important;
			}
			.body-text-1 {
				font-size: 14px !important;
				line-height: 21px !important;
				letter-spacing: 0.5px !important;
			}
			.body-text-2 {
				font-size: 13px !important;
				line-height: 19.5px !important;
				letter-spacing: 0.25px !important;
			}
			.body-text-3 {
				font-size: 10px !important;
				line-height: 15px !important;
			}
			.label {
				color: $font-header;
				font-family: 'RobotoMedium';
				margin-right: 4px;
			}

			header {
				display: flex;
				align-items: center;
				padding: 10px;

				.ja-logo {
					height: 65px;
				}
			}

			h1 {
				margin-top: 0;
				margin-bottom: 18px;
			}

			.student-info-wrapper {
				width: 90%;
				max-width: 1000px;
				margin-top: 16px;
				display: flex;
				align-items: flex-start;

				.avatar-wrapper {
					position: relative;

					.financial-status {
						position: absolute;
						bottom: -5px;
						right: -10px;
					}
				}

				.student-info {
					padding: 0 50px;
				}
			}

			.simulation-info {
				width: 45%;
			}
			.container-career-goals {
				width: 90%;
				max-width: 1000px;
				display: flex;
				flex-wrap: wrap;
				margin-top: 25px;
				gap: 40px;
			}
			.career-info {
				width: 45%;

				.info-entry {
					width: 100%;
					margin-top: 8px;
					display: flex;

					.body-text-2 {
						display: inline-block;
					}

					&.nmi-entry {
						display: inline;
						margin-right: 20px;
					}
				}
			}

			.info-entry {
				width: 100%;
				margin-top: 8px;

				.body-text-2 {
					display: inline-block;
				}
			}

			.dashboard-wrapper {
				width: 100%;

				.heading-3 {
					padding-left: 5%;
					margin-top: 28px;
					margin-bottom: 15px;
				}

				.categories-grid {
					display: table;
					width: 100%;
					.grid-row {
						display: table-row;

						.header {
							display: table-cell;
							height: 40px;
							font-family: 'RobotoMedium';
							padding-right: 8px;
							white-space: nowrap;
							&:first-child {
								padding-left: 48px;
							}
						}

						.grid-item {
							display: table-cell;
							padding: 8px 24px 8px 0;
							&.category-name {
								font-family: 'RobotoMedium';
								padding-left: 48px;
							}
							&.odd {
								background-color: $table-row-bgcolor;
							}
						}
					}
				}
			}

			.financial-overview {
				width: 90%;
				margin: 48px 0 32px 0;

				.heading-3 {
					margin-top: 0;
					margin-bottom: 32px;
				}
				.charts-wrapper {
					width: 100%;
					display: flex;

					.chart-wrapper {
						width: 50%;
						&:first-child {
							margin-right: 16px;
							margin-bottom: 100px;
						}
						::v-deep .chart-title-wrapper {
							.heading-5 {
								font-size: 13px;
								letter-spacing: 0.5px;
								line-height: normal;
								margin-top: 10px;
							}
							.heading-4 {
								font-size: 16px;
							}
						}
						::v-deep .additional-info {
							white-space: pre-line;
							font-size: 12px;
							display: contents;
						}
					}
				}
			}

			.logo-wrapper {
				width: 90%;
				display: none;
				margin-bottom: 32px;
			}
		}
	}

	footer {
		page-break-after: always;
		.footer-item {
			flex-grow: 1;
		}
		button {
			&:last-child {
				margin-left: 16px;
			}
		}
	}
	.modal-selection {
		padding: 30px;
		height: 540px;
		z-index: 999;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		&__content {
			height: 540px;
			overflow: auto;
			display: flex;
			flex-wrap: wrap;
			gap: 15px;
			margin: 15px;
		}
		.heading-3 {
			text-align: center;
		}
		.select-student {
			width: 210px;
			&__checkbox {
				font-family: 'RobotoRegular';
				font-size: 15px;
				line-height: 23px;
				align-items: center;
				position: relative;
				width: 20px;
				height: 20px;
				border-radius: 50%;
				border: 2px solid #ccc;
				background-color: #fff;
				cursor: pointer;
				&:checked {
					background-color: #007bff;
				}
			}
		}
	}
}
@media print {
	content {
		.print-preview {
			margin-bottom: 25px;
		}
	}
	.report-page {
		height: auto;
		margin: auto;
	}
	.print-preview {
		page-break-after: always;
	}
	.chart-legend-color {
		height: 20px;
		width: 20px;
	}
	footer {
		display: none;
	}
}
</style>
